/**
 * Clustering Utilities for Polo Analysis
 * K-means, PCA, and feature extraction for raw aggregate data
 */

/**
 * Feature metadata for clustering
 * Maps aggregate keys to human-readable labels and categories
 */
export const FEATURE_META = {
  // Connectivity - Polo level
  equipamento_urbano: { label: 'Equip. Urbano', category: 'connectivity', type: 'boolean' },
  terminal_de_transporte: { label: 'Terminal Transp.', category: 'connectivity', type: 'boolean' },

  // Connectivity - Accessibility
  estacionamento_meio_fio_pct: { label: 'Estac. Meio-fio', category: 'connectivity', type: 'percent' },
  estacionamento_zona_azul_pct: { label: 'Zona Azul', category: 'connectivity', type: 'percent' },
  estacionamento_recuo_pct: { label: 'Estac. Recuo', category: 'connectivity', type: 'percent' },
  estacionamento_garagem_pct: { label: 'Estac. Garagem', category: 'connectivity', type: 'percent' },
  estacionamento_moto_pct: { label: 'Estac. Moto', category: 'connectivity', type: 'percent' },
  has_ponto_onibus: { label: 'Ponto Ônibus', category: 'connectivity', type: 'boolean' },
  has_ponto_taxi: { label: 'Ponto Táxi', category: 'connectivity', type: 'boolean' },
  has_ciclovia: { label: 'Ciclovia', category: 'connectivity', type: 'boolean' },

  // Connectivity - Street vs Building
  fruicao_publica_pct: { label: 'Fruição Pública', category: 'connectivity', type: 'percent' },
  testada_grande_pct: { label: 'Testada >10m', category: 'connectivity', type: 'percent' },
  testada_media_pct: { label: 'Testada 5-10m', category: 'connectivity', type: 'percent' },
  testada_pequena_pct: { label: 'Testada <5m', category: 'connectivity', type: 'percent' },
  fachada_ativa_pct: { label: 'Fachada Ativa', category: 'connectivity', type: 'percent' },

  // Diversity - Uses
  fim_de_semana_pct: { label: 'Fim de Semana', category: 'diversity', type: 'percent' },
  apos_19_pct: { label: 'Após 19h', category: 'diversity', type: 'percent' },
  uso_terreo_unique_count: { label: 'Tipos de Uso', category: 'diversity', type: 'count' },
  uso_superior_pct: { label: 'Uso Superior', category: 'diversity', type: 'percent' },

  // Diversity - Architecture
  has_edificacao_tombada: { label: 'Edif. Tombada', category: 'diversity', type: 'boolean' },

  // Comfort - Sensorial
  arborizacao_pct: { label: 'Arborização', category: 'comfort', type: 'percent' },
  protecao_sol_chuva_pct: { label: 'Proteção Sol/Chuva', category: 'comfort', type: 'percent' },
  cabeamento_enterrado_pct: { label: 'Fiação Subterrânea', category: 'comfort', type: 'percent' },

  // Comfort - Passeio
  faixa_livre_pedestres_pct: { label: 'Faixa Livre', category: 'comfort', type: 'percent' },
  piso_acessivel_pct: { label: 'Piso Acessível', category: 'comfort', type: 'percent' },
  degrau_entre_calcadas_pct: { label: 'Degraus', category: 'comfort', type: 'percent' },
  permanencia_na_calcada_pct: { label: 'Área Permanência', category: 'comfort', type: 'percent' },

  // Comfort - Security
  muro_pct: { label: 'Muros', category: 'comfort', type: 'percent' },
  semaforo_pedestres_pct: { label: 'Semáf. Pedestres', category: 'comfort', type: 'percent' },
  semaforo_veiculos_pct: { label: 'Semáf. Veículos', category: 'comfort', type: 'percent' },
  faixa_pedestres_pct: { label: 'Faixa Pedestres', category: 'comfort', type: 'percent' },
  entrada_de_veiculos_pct: { label: 'Entrada Veículos', category: 'comfort', type: 'percent' }
}

// Features to use for clustering (excluding distributions for simplicity)
export const CLUSTERING_FEATURES = Object.keys(FEATURE_META)

export const CATEGORY_COLORS = {
  connectivity: '#dc3545',
  diversity: '#0dcaf0',
  comfort: '#198754'
}

/**
 * Extract numerical feature vector from polo aggregates
 */
export function extractFeatureVector(polo) {
  const agg = polo.aggregates
  return CLUSTERING_FEATURES.map(key => {
    const value = agg[key]
    const meta = FEATURE_META[key]

    if (meta.type === 'boolean') {
      return value ? 100 : 0
    } else if (meta.type === 'count') {
      return value || 0
    } else {
      return value || 0
    }
  })
}

/**
 * Extract feature matrix from all polos
 * Returns { matrix: number[][], polos: polo[], featureNames: string[] }
 */
export function extractFeatureMatrix(polos) {
  const matrix = polos.map(extractFeatureVector)
  return {
    matrix,
    polos,
    featureNames: CLUSTERING_FEATURES
  }
}

/**
 * Normalize features using z-score normalization
 * Returns normalized matrix and stats for denormalization
 */
export function normalizeFeatures(matrix) {
  const n = matrix.length
  const m = matrix[0].length

  // Calculate mean and std for each feature
  const stats = []
  for (let j = 0; j < m; j++) {
    const values = matrix.map(row => row[j])
    const mean = values.reduce((a, b) => a + b, 0) / n
    const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / n
    const std = Math.sqrt(variance) || 1 // Avoid division by zero
    stats.push({ mean, std })
  }

  // Normalize
  const normalized = matrix.map(row =>
    row.map((val, j) => (val - stats[j].mean) / stats[j].std)
  )

  return { normalized, stats }
}

/**
 * Calculate Euclidean distance between two vectors
 */
function euclideanDistance(a, b) {
  return Math.sqrt(a.reduce((sum, val, i) => sum + (val - b[i]) ** 2, 0))
}

/**
 * Principal Component Analysis (PCA)
 * Returns first 2 principal components for visualization
 */
export function computePCA(normalizedData) {
  const n = normalizedData.length
  const m = normalizedData[0].length

  // Compute covariance matrix
  const cov = []
  for (let i = 0; i < m; i++) {
    cov[i] = []
    for (let j = 0; j < m; j++) {
      let sum = 0
      for (let k = 0; k < n; k++) {
        sum += normalizedData[k][i] * normalizedData[k][j]
      }
      cov[i][j] = sum / (n - 1)
    }
  }

  // Power iteration to find top 2 eigenvectors
  const eigenvectors = []
  const eigenvalues = []

  for (let ev = 0; ev < 2; ev++) {
    // Start with random vector
    let vec = Array(m).fill(0).map(() => Math.random() - 0.5)
    let norm = Math.sqrt(vec.reduce((s, v) => s + v * v, 0))
    vec = vec.map(v => v / norm)

    // Power iteration
    for (let iter = 0; iter < 100; iter++) {
      // Multiply by covariance matrix
      const newVec = vec.map((_, i) =>
        cov[i].reduce((sum, c, j) => sum + c * vec[j], 0)
      )

      // Deflate by previous eigenvectors
      for (let prev = 0; prev < ev; prev++) {
        const dot = newVec.reduce((s, v, i) => s + v * eigenvectors[prev][i], 0)
        for (let i = 0; i < m; i++) {
          newVec[i] -= dot * eigenvectors[prev][i]
        }
      }

      // Normalize
      norm = Math.sqrt(newVec.reduce((s, v) => s + v * v, 0))
      vec = newVec.map(v => v / norm)
    }

    // Compute eigenvalue
    const transformed = vec.map((_, i) => cov[i].reduce((sum, c, j) => sum + c * vec[j], 0))
    const eigenvalue = transformed.reduce((s, v, i) => s + v * vec[i], 0)

    eigenvectors.push(vec)
    eigenvalues.push(eigenvalue)
  }

  // Project data onto first 2 PCs
  const projected = normalizedData.map(row => [
    row.reduce((sum, val, i) => sum + val * eigenvectors[0][i], 0),
    row.reduce((sum, val, i) => sum + val * eigenvectors[1][i], 0)
  ])

  // Get top loadings for interpretation
  const loadings = [0, 1].map(pc => {
    const indexed = eigenvectors[pc].map((val, i) => ({
      feature: CLUSTERING_FEATURES[i],
      loading: val,
      absLoading: Math.abs(val)
    }))
    indexed.sort((a, b) => b.absLoading - a.absLoading)
    return indexed.slice(0, 5) // Top 5 loadings
  })

  return { projected, eigenvectors, eigenvalues, loadings }
}

/**
 * Compute cluster profiles (mean feature values per cluster)
 * Returns denormalized values for interpretability
 */
export function computeClusterProfiles(data, assignments, stats, k) {
  const m = data[0].length
  const profiles = []

  for (let c = 0; c < k; c++) {
    const clusterIndices = data
      .map((_, i) => i)
      .filter(i => assignments[i] === c)

    if (clusterIndices.length === 0) {
      profiles.push(CLUSTERING_FEATURES.map(() => 0))
      continue
    }

    // Compute mean for each feature (denormalized)
    const profile = CLUSTERING_FEATURES.map((_, j) => {
      const normalizedMean = clusterIndices.reduce((sum, i) => sum + data[i][j], 0) / clusterIndices.length
      // Denormalize
      return normalizedMean * stats[j].std + stats[j].mean
    })

    profiles.push(profile)
  }

  return profiles
}

/**
 * Generate cluster labels based on distinctive features
 */
export function generateClusterLabels(profiles, globalMeans) {
  return profiles.map((profile, clusterIdx) => {
    // Find features that are notably higher or lower than average
    const distinctive = CLUSTERING_FEATURES
      .map((feature, i) => ({
        feature,
        value: profile[i],
        diff: profile[i] - globalMeans[i],
        meta: FEATURE_META[feature]
      }))
      .filter(f => Math.abs(f.diff) > 15) // At least 15% difference
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
      .slice(0, 3)

    if (distinctive.length === 0) {
      return `Cluster ${clusterIdx + 1}`
    }

    const traits = distinctive.map(d => {
      const direction = d.diff > 0 ? '+' : '-'
      return `${direction}${d.meta.label}`
    })

    return traits.join(', ')
  })
}

/**
 * Compute distance matrix for hierarchical clustering
 */
export function computeDistanceMatrix(data) {
  const n = data.length
  const matrix = []

  for (let i = 0; i < n; i++) {
    matrix[i] = []
    for (let j = 0; j < n; j++) {
      matrix[i][j] = i === j ? 0 : euclideanDistance(data[i], data[j])
    }
  }

  return matrix
}

// Re-export hierarchical clustering from correlation.js
export { hierarchicalClusteringOrder } from './correlation.js'

/**
 * Hierarchical clustering that returns both tree and ability to cut at k
 * Uses Ward's linkage (minimizes within-cluster variance)
 * @param {number[][]} distanceMatrix - Pairwise distances (squared Euclidean recommended)
 * @returns {Object} - { getAssignments: (k) => number[], order: number[] }
 */
export function hierarchicalClustering(distanceMatrix) {
  const n = distanceMatrix.length
  if (n <= 1) return { getAssignments: () => [0], order: [0] }
  if (n === 2) return { getAssignments: (k) => k >= 2 ? [0, 1] : [0, 0], order: [0, 1] }

  // Initialize clusters: each item starts as its own cluster
  const clusters = Array(n).fill(null).map((_, i) => ({
    id: i,
    elements: [i],
    left: null,
    right: null,
    height: 0
  }))

  // For Ward's method, we use squared distances
  // Convert distance matrix to squared distances for Ward's
  const clusterDist = distanceMatrix.map(row => row.map(d => d * d))

  // Track which clusters are still active
  const active = new Set(Array(n).fill(null).map((_, i) => i))

  // Track merge history for cutting
  const mergeHistory = []
  let nextClusterId = n

  // Merge until only one cluster remains
  while (active.size > 1) {
    // Find minimum distance pair (using Ward's criterion)
    let minDist = Infinity
    let minI = -1
    let minJ = -1

    const activeArr = [...active]
    for (let i = 0; i < activeArr.length; i++) {
      for (let j = i + 1; j < activeArr.length; j++) {
        const ci = activeArr[i]
        const cj = activeArr[j]
        // Ward's criterion: weighted by cluster sizes
        const ni = clusters[ci].elements.length
        const nj = clusters[cj].elements.length
        const wardDist = (ni * nj) / (ni + nj) * clusterDist[ci][cj]
        if (wardDist < minDist) {
          minDist = wardDist
          minI = ci
          minJ = cj
        }
      }
    }

    // Record merge
    mergeHistory.push({
      merged: [minI, minJ],
      newCluster: nextClusterId,
      distance: Math.sqrt(minDist) // Store actual distance for interpretability
    })

    // Merge clusters minI and minJ
    const merged = {
      id: nextClusterId,
      elements: [...clusters[minI].elements, ...clusters[minJ].elements],
      left: clusters[minI],
      right: clusters[minJ],
      height: Math.sqrt(minDist)
    }

    // Update distance matrix using Lance-Williams formula for Ward's method
    clusterDist.push([])
    const ni = clusters[minI].elements.length
    const nj = clusters[minJ].elements.length
    const nij = ni + nj

    for (let k = 0; k < nextClusterId; k++) {
      if (active.has(k) && k !== minI && k !== minJ) {
        const nk = clusters[k].elements.length
        // Lance-Williams formula for Ward's linkage
        const newDist = ((ni + nk) * clusterDist[minI][k] +
                         (nj + nk) * clusterDist[minJ][k] -
                         nk * clusterDist[minI][minJ]) / (nij + nk)
        clusterDist[nextClusterId][k] = newDist
        clusterDist[k][nextClusterId] = newDist
      } else {
        clusterDist[nextClusterId][k] = Infinity
        if (!clusterDist[k]) clusterDist[k] = []
        clusterDist[k][nextClusterId] = Infinity
      }
    }
    clusterDist[nextClusterId][nextClusterId] = 0

    clusters.push(merged)
    active.delete(minI)
    active.delete(minJ)
    active.add(nextClusterId)
    nextClusterId++
  }

  // Extract leaf order from final tree (in-order traversal)
  function getLeafOrder(node) {
    if (!node.left && !node.right) {
      return node.elements
    }
    return [...getLeafOrder(node.left), ...getLeafOrder(node.right)]
  }

  const finalCluster = clusters[clusters.length - 1]
  const order = getLeafOrder(finalCluster)

  // Function to get assignments at k clusters by "cutting" the tree
  function getAssignments(k) {
    if (k >= n) {
      // Each point is its own cluster
      return Array(n).fill(null).map((_, i) => i)
    }
    if (k <= 1) {
      // All points in one cluster
      return Array(n).fill(0)
    }

    // Start with all points in separate clusters
    const pointToCluster = Array(n).fill(null).map((_, i) => i)

    // Replay merges, stopping when we reach k clusters
    let numClusters = n
    for (const merge of mergeHistory) {
      if (numClusters <= k) break

      const [c1, c2] = merge.merged
      const newClusterId = Math.min(
        ...clusters[c1].elements.map(e => pointToCluster[e]),
        ...clusters[c2].elements.map(e => pointToCluster[e])
      )

      // Assign all points in both clusters to the same cluster
      for (const elem of clusters[c1].elements) {
        pointToCluster[elem] = newClusterId
      }
      for (const elem of clusters[c2].elements) {
        pointToCluster[elem] = newClusterId
      }

      numClusters--
    }

    // Renumber clusters to be 0, 1, 2, ... k-1
    const uniqueClusters = [...new Set(pointToCluster)]
    const clusterMap = {}
    uniqueClusters.forEach((c, i) => { clusterMap[c] = i })

    return pointToCluster.map(c => clusterMap[c])
  }

  return { getAssignments, order, mergeHistory }
}

/**
 * Cluster colors for visualization
 */
export const CLUSTER_COLORS = [
  '#e41a1c', // red
  '#377eb8', // blue
  '#4daf4a', // green
  '#984ea3', // purple
  '#ff7f00', // orange
  '#ffff33', // yellow
  '#a65628', // brown
  '#f781bf'  // pink
]
